"use client";

import { useEffect, useState } from "react";
import { useMicrosoftOAuthSetup } from "../../hooks/use-api.js";
import {
  getStoredCodeVerifier,
  clearStoredCodeVerifier,
} from "../../lib/oauth-utils.js";
import { Card, CardContent, CardHeader, CardTitle } from "../card.js";
import { Alert, AlertDescription } from "../alert.js";
import { Button } from "../button.js";
import { Calendar, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useToast } from "../../hooks/use-toast.js";
import { getBrowserSupabaseClient } from "../../lib/supabase-browser.js";

export default function MicrosoftSetupPage() {
  const router = useRouter();
  const { toast } = useToast();
  const microsoftOAuthSetup = useMicrosoftOAuthSetup();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check for OAuth callback parameters in URL fragment
      const urlHash = new URLSearchParams(window.location.hash.substring(1));
      const code = urlHash.get("code");
      const state = urlHash.get("state");
      const errorParam = urlHash.get("error");

      if (errorParam) {
        setError(decodeURIComponent(errorParam));

        return;
      }

      if (code) {
        // Get session after OAuth redirect to ensure it's available
        const supabase = getBrowserSupabaseClient();
        try {
          // This will get the session from localStorage/cookies
          await supabase.auth.getSession();
        } catch (error) {
          console.warn("Error getting session:", error);
        }
        
        // Small delay to allow session to restore after OAuth redirect
        await new Promise(resolve => setTimeout(resolve, 500));
        await exchangeCodeForToken(code, state || undefined);
      } else {
        // No OAuth callback, redirect to setup
        router.push("/unifiedtime/setup");
      }
    };

    handleOAuthCallback();
  }, [router]);

  const exchangeCodeForToken = async (code: string, state?: string) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const isSecondAccount = state === "secondAccount";
      const codeVerifier = getStoredCodeVerifier(isSecondAccount);
      if (!codeVerifier) {
        throw new Error("PKCE code verifier missing");
      }

      await microsoftOAuthSetup.mutateAsync({
        code,
        code_verifier: codeVerifier,
        isSecondAccount,
      });

      clearStoredCodeVerifier(isSecondAccount);

      const accountType = isSecondAccount ? "secondary" : "primary";
      setSuccessMessage(
        `${accountType.charAt(0).toUpperCase() + accountType.slice(1)} Microsoft account connected successfully!`,
      );

      toast({
        title: "Success",
        description: `${accountType.charAt(0).toUpperCase() + accountType.slice(1)} Microsoft account connected successfully!`,
      });
    } catch (err: any) {
      const errorMessage =
        err.message || "Failed to connect Microsoft calendar";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSetup = () => {
    router.push("/unifiedtime/setup");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center space-y-4 p-6">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p>Connecting Microsoft Calendar...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold">Microsoft Calendar Setup</h1>
        <p className="text-muted-foreground">
          Connect your Microsoft Calendar to UnifiedTime
        </p>
      </div>

      {successMessage && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Microsoft Calendar Connection</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            {successMessage ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Your Microsoft calendar is successfully connected. You can now
                  view your events in the unified calendar.
                </p>
                <Button onClick={handleBackToSetup} className="w-full">
                  Back to Setup
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  There was an issue connecting your Microsoft calendar. Please
                  try again.
                </p>
                <Button onClick={handleBackToSetup} className="w-full">
                  Back to Setup
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
